'use client';
// Bloomberg-style single-symbol chart used by /hedge-lab focused view.
// Two synced panels (price on top, RSI/momentum on bottom) sharing X axis +
// cursor via Recharts `syncId`. Period toggle (D/W/M) drives bar frequency
// on both panels simultaneously. No profit/loss zones; the chart is OHLC
// only — visual noise from strategy payoff bands belongs in PayoffChart.
//
// Price panel renders OHLC as:
//   - high-low translucent grey band (the day's range)
//   - close line in white (the headline level)
//   - strike overlays from the selected hedge strategy
//   - spot reference line
//
// RSI/momentum panel renders RSI(14) on left axis (0-100, 70/30 reference
// lines) and 12-1 momentum on right axis (% return).

import { useMemo } from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  LineChart,
  XAxis,
  YAxis,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from 'recharts';
import { useResource } from '@/lib/use-resource';
import { resamplePrices, type Period, type PricePoint } from '@/lib/price-resample';
import type { HedgeStrategy, HedgeLeg } from '@/lib/hedge-structures';

// Y-axis gutter widths — IDENTICAL on both panels so the chart-drawing area
// (and thus the date X-axis) lines up exactly. The right gutter on the price
// panel is consumed by strike-label text; the right gutter on the oscillator
// panel is consumed by the momentum scale. Both 110.
const LEFT_AXIS_W = 56;
const RIGHT_GUTTER = 110;

const RSI_PERIOD = 14;
const MOMENTUM_BARS = { D: { lookback: 252, skip: 21 }, W: { lookback: 52, skip: 4 }, M: { lookback: 12, skip: 1 } } as const;
const VIEW_BARS = { D: 180, W: 156, M: 120 } as const;

interface ApiResponse { prices?: PricePoint[] }

interface StrikeOverlay { strike: number; label: string; color: string }

function strikeOverlayForLeg(leg: HedgeLeg): StrikeOverlay {
  const isCall = leg.asset === 'call';
  const verb = leg.action === 'sell' ? 'Sell' : 'Buy';
  const sideLetter = isCall ? 'C' : 'P';
  return {
    strike: leg.strike!,
    label: `${verb} $${leg.strike} ${sideLetter}`,
    color: isCall ? '#ef4444' : '#10b981',
  };
}

function overlaysFromStrategy(strategy: HedgeStrategy | null): StrikeOverlay[] {
  if (!strategy) return [];
  const out: StrikeOverlay[] = [];
  for (const leg of strategy.legs) {
    if (leg.asset === 'stock' || leg.strike == null) continue;
    out.push(strikeOverlayForLeg(leg));
  }
  return out;
}

function computeRsi(prices: number[]): Array<number | null> {
  const out: Array<number | null> = new Array(prices.length).fill(null);
  if (prices.length < RSI_PERIOD + 1) return out;
  let gainAvg = 0, lossAvg = 0;
  for (let i = 1; i <= RSI_PERIOD; i++) {
    const d = prices[i] - prices[i - 1];
    if (d > 0) gainAvg += d;
    else lossAvg -= d;
  }
  gainAvg /= RSI_PERIOD;
  lossAvg /= RSI_PERIOD;
  out[RSI_PERIOD] = lossAvg === 0 ? 100 : 100 - 100 / (1 + gainAvg / lossAvg);
  for (let i = RSI_PERIOD + 1; i < prices.length; i++) {
    const d = prices[i] - prices[i - 1];
    const gain = d > 0 ? d : 0;
    const loss = d < 0 ? -d : 0;
    gainAvg = (gainAvg * (RSI_PERIOD - 1) + gain) / RSI_PERIOD;
    lossAvg = (lossAvg * (RSI_PERIOD - 1) + loss) / RSI_PERIOD;
    out[i] = lossAvg === 0 ? 100 : 100 - 100 / (1 + gainAvg / lossAvg);
  }
  return out;
}

function computeMomentum(prices: number[], period: Period): Array<number | null> {
  const { lookback, skip } = MOMENTUM_BARS[period];
  const out: Array<number | null> = new Array(prices.length).fill(null);
  for (let i = lookback; i < prices.length; i++) {
    const recent = prices[i - skip];
    const old = prices[i - lookback];
    if (recent > 0 && old > 0) out[i] = (recent / old - 1) * 100;
  }
  return out;
}

/**
 * Custom candlestick shape for Recharts. The Bar's bounding box (x/y/width/height)
 * is computed by Recharts from the `hlBand` data tuple [low, high], so:
 *   - x = bar's left pixel
 *   - y = pixel of HIGH (top of bar)
 *   - height = pixel distance from HIGH to LOW
 *   - width = bar slot width
 * We map open/close into the same pixel space using the high-low proportion.
 *
 * Convention: green if close ≥ open (up day), red otherwise.
 */
interface CandleShapeProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  payload?: { open?: number | null; high?: number | null; low?: number | null; close: number };
}
function CandleShape(props: CandleShapeProps): React.ReactElement | null {
  const { x = 0, y = 0, width = 0, height = 0, payload } = props;
  if (!payload || payload.high == null || payload.low == null) return null;
  const { open, high, low, close } = payload;
  const o = open ?? close;
  const isUp = close >= o;
  const color = isUp ? '#10b981' : '#ef4444';
  const cx = x + width / 2;
  if (high === low) {
    return <line x1={cx} y1={y} x2={cx} y2={y + height} stroke={color} strokeWidth={1} />;
  }
  const range = high - low;
  // y is the pixel of high; pixel-per-data-unit = height / range
  const openY = y + ((high - o) / range) * height;
  const closeY = y + ((high - close) / range) * height;
  const bodyTop = Math.min(openY, closeY);
  const bodyHeight = Math.max(1, Math.abs(openY - closeY));
  const bodyWidth = Math.max(1, width * 0.65);
  const bodyX = x + (width - bodyWidth) / 2;
  return (
    <g>
      <line x1={cx} y1={y} x2={cx} y2={y + height} stroke={color} strokeWidth={1} />
      <rect x={bodyX} y={bodyTop} width={bodyWidth} height={bodyHeight} fill={color} stroke={color} />
    </g>
  );
}

export function StockChartCombined({
  symbol,
  strategy,
  period = 'D',
  priceHeight = 400,
  oscHeight = 200,
}: {
  symbol: string;
  strategy: HedgeStrategy | null;
  period?: Period;
  priceHeight?: number;
  oscHeight?: number;
}) {
  const { data, loading } = useResource<ApiResponse>(`/api/prices?symbol=${symbol}`);

  const series = useMemo(() => {
    const raw = data?.prices ?? [];
    if (raw.length === 0) return [];
    // Lookback must include a warm-up window (252 daily / 52 weekly / 12 monthly
    // bars) BEFORE the visible range so the 12-1 momentum line is fully populated
    // across the chart. Steve directive 2026-05-09: blue line should go all the
    // way back, not start partway in.
    const lookbackDays = period === 'D' ? 730 : period === 'W' ? 365 * 5 : 365 * 15;
    const trimmed = raw.slice(-lookbackDays);
    const resampled = resamplePrices(trimmed, period);
    const closes = resampled.map((p) => p.close);
    const rsi = computeRsi(closes);
    const mom = computeMomentum(closes, period);
    const start = Math.max(0, resampled.length - VIEW_BARS[period]);
    return resampled.slice(start).map((p, i) => ({
      date: p.date,
      open: p.open ?? p.close,
      high: p.high ?? p.close,
      low: p.low ?? p.close,
      close: p.close,
      // For Recharts Area between high and low, supply a tuple [low, high]
      hlBand: [p.low ?? p.close, p.high ?? p.close] as [number, number],
      rsi: rsi[start + i],
      momentum: mom[start + i],
    }));
  }, [data, period]);

  if (loading) return <div className="text-[11px] text-[#64748b] text-center py-12">loading {symbol}…</div>;
  if (series.length === 0) return <div className="text-[11px] text-[#64748b] text-center py-12">no price data for {symbol}</div>;

  const overlays = overlaysFromStrategy(strategy);
  const spot = strategy?.spot ?? series[series.length - 1]?.close ?? null;

  // Price Y-axis domain (with strike padding so overlay lines fit)
  const allLevels = [
    ...series.map((s) => s.high),
    ...series.map((s) => s.low),
    ...overlays.map((o) => o.strike),
    ...(spot != null ? [spot] : []),
  ].filter((v): v is number => Number.isFinite(v));
  const yMin = allLevels.length ? Math.min(...allLevels) * 0.97 : 0;
  const yMax = allLevels.length ? Math.max(...allLevels) * 1.03 : 1;

  const periodLabel = period === 'D' ? '1Y daily' : period === 'W' ? '3Y weekly' : '10Y monthly';
  const syncId = `chart-${symbol}-${period}`;

  return (
    <div className="border border-[#1e293b] rounded-lg bg-[#0f141c] overflow-hidden">
      {/* Price panel ─────────────────────────────────────────────────── */}
      <div className="px-3 pt-3">
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-[11px] uppercase tracking-wide text-[#94a3b8] font-semibold">
            <span className="font-mono text-amber-300 mr-2">{symbol}</span>
            price · {periodLabel}
            {strategy && <span className="text-[#64748b] normal-case ml-2">strikes from {strategy.name}</span>}
          </div>
          <div className="text-[9px] text-[#64748b]">candles · green = up · red = down · wick = high-low</div>
        </div>
        <div style={{ height: priceHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={series} syncId={syncId} margin={{ top: 8, right: RIGHT_GUTTER, bottom: 0, left: 0 }}>
              <XAxis dataKey="date" stroke="#475569" tick={{ fill: '#64748b', fontSize: 9 }} interval={Math.max(0, Math.floor(series.length / 8))} />
              <YAxis stroke="#475569" tick={{ fill: '#64748b', fontSize: 9 }} domain={[yMin, yMax]} width={LEFT_AXIS_W} tickFormatter={(v: number) => `$${v.toFixed(0)}`} />
              <Tooltip
                contentStyle={{ backgroundColor: '#0a0e14', border: '1px solid #334155', borderRadius: 6, fontSize: 11, color: '#e2e8f0' }}
                labelStyle={{ color: '#94a3b8' }}
                formatter={(v, name) => {
                  if (name === 'hlBand') return ['', ''];
                  if (typeof v !== 'number') return ['—', String(name ?? '')];
                  return [`$${v.toFixed(2)}`, String(name ?? '')];
                }}
              />
              {/* Candlestick body+wick rendered via custom shape on a Bar
                  driven by the [low, high] tuple (`hlBand`). */}
              <Bar dataKey="hlBand" shape={(props: object) => <CandleShape {...(props as CandleShapeProps)} /> } isAnimationActive={false} />
              {spot != null && (
                <ReferenceLine
                  y={spot}
                  stroke="#c9a84c"
                  strokeDasharray="2 2"
                  label={{ value: `spot $${spot.toFixed(2)}`, fill: '#c9a84c', fontSize: 10, position: 'right' }}
                />
              )}
              {overlays.map((o, i) => (
                <ReferenceLine
                  key={`${o.strike}-${i}`}
                  y={o.strike}
                  stroke={o.color}
                  strokeDasharray="3 3"
                  label={{ value: o.label, fill: o.color, fontSize: 10, position: 'right' }}
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Divider rail. */}
      <div className="border-t border-[#1e293b] mx-3 mt-1" />

      {/* RSI/momentum panel — IDENTICAL X-axis gutter to the price panel
          (left LEFT_AXIS_W + right RIGHT_GUTTER) so dates line up vertically. */}
      <div className="px-3 pt-2 pb-3">
        <div className="flex items-baseline justify-between mb-1">
          <div className="text-[10px] uppercase tracking-wider text-[#94a3b8]">
            RSI(14) + 12-1 momentum
          </div>
          <div className="text-[9px] text-[#64748b]">RSI yellow (left axis 0-100) · momentum blue (% return) · 70/30 levels</div>
        </div>
        <div style={{ height: oscHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} syncId={syncId} margin={{ top: 4, right: RIGHT_GUTTER, bottom: 0, left: 0 }}>
              <XAxis dataKey="date" stroke="#475569" tick={{ fill: '#64748b', fontSize: 9 }} interval={Math.max(0, Math.floor(series.length / 8))} />
              {/* Single LEFT axis so the right gutter (RIGHT_GUTTER margin)
                  matches the price panel exactly. Momentum's scale lives in
                  the tooltip + the legend label rather than a 2nd axis. */}
              <YAxis yAxisId="rsi" domain={[0, 100]} tick={{ fill: '#64748b', fontSize: 9 }} width={LEFT_AXIS_W} stroke="#475569" tickFormatter={(v: number) => v.toFixed(0)} />
              <YAxis yAxisId="mom" hide domain={['auto', 'auto']} />
              <ReferenceLine yAxisId="rsi" y={70} stroke="#dc2626" strokeDasharray="3 3" strokeOpacity={0.45} label={{ value: '70', fill: '#dc2626', fontSize: 9, position: 'right' }} />
              <ReferenceLine yAxisId="rsi" y={30} stroke="#16a34a" strokeDasharray="3 3" strokeOpacity={0.45} label={{ value: '30', fill: '#16a34a', fontSize: 9, position: 'right' }} />
              <Tooltip
                contentStyle={{ backgroundColor: '#0a0e14', border: '1px solid #334155', borderRadius: 6, fontSize: 11, color: '#e2e8f0' }}
                labelStyle={{ color: '#94a3b8' }}
                formatter={(v, name) => {
                  if (typeof v !== 'number') return ['—', String(name ?? '')];
                  if (name === 'momentum') return [`${v.toFixed(1)}%`, '12-1 mom'];
                  return [v.toFixed(1), 'RSI'];
                }}
              />
              <Legend wrapperStyle={{ fontSize: 10 }} iconType="plainline" />
              <Line yAxisId="rsi" type="monotone" dataKey="rsi" stroke="#fbbf24" strokeWidth={1.4} dot={false} isAnimationActive={false} name="RSI" />
              <Line yAxisId="mom" type="monotone" dataKey="momentum" stroke="#60a5fa" strokeWidth={1.4} dot={false} isAnimationActive={false} name="momentum" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
